import React, { useEffect, useMemo, useState } from 'react';
import { trainingReportApi } from '../../services/trainingReportApi';
import {
  buildResultsPayload,
  calculateLocalResultsSummary,
  createResultsDraft,
  normalizeResultValue,
  validateResultsRows,
} from './trainingReportResults';
import './TrainingReportResults.css';

export const TrainingReportResultsStep = ({
  idReporte,
  accessToken,
  report,
  setReport,
  onPrevious,
}) => {
  const [rows, setRows] = useState(() => createResultsDraft(report));
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [serverSummary, setServerSummary] = useState(null);

  const localSummary = useMemo(
    () => calculateLocalResultsSummary(rows),
    [rows],
  );
  const summary = serverSummary || localSummary;

  useEffect(() => {
    setRows(createResultsDraft(report));
    setServerSummary(null);
  }, [report]);

  useEffect(() => {
    let active = true;

    const loadSummary = async () => {
      if (!accessToken) return;
      try {
        const result = await trainingReportApi.summary(idReporte, accessToken);
        if (active) setServerSummary(result);
      } catch (error) {
        console.error('No se pudo cargar el resumen del reporte:', error);
      }
    };

    loadSummary();
    return () => {
      active = false;
    };
  }, [accessToken, idReporte, report]);

  const changeGrade = (participantId, field, value) => {
    setRows((current) =>
      current.map((row) =>
        row.id_reporte_participante === participantId
          ? { ...row, [field]: value }
          : row,
      ),
    );
    setServerSummary(null);
    setFieldErrors((current) => {
      if (!current[participantId]) return current;
      const next = { ...current };
      const participantErrors = { ...next[participantId] };
      delete participantErrors[field];
      if (
        !participantErrors.calificacion_diagnostica &&
        !participantErrors.calificacion_final
      ) {
        delete next[participantId];
      } else {
        next[participantId] = participantErrors;
      }
      return next;
    });
    setStatus(null);
  };

  const normalizeOnBlur = (participantId, field, value) => {
    changeGrade(participantId, field, normalizeResultValue(value));
  };

  const saveResults = async () => {
    if (!accessToken || saving || rows.length === 0) return;

    const validationErrors = validateResultsRows(rows);
    setFieldErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setStatus({
        type: 'error',
        message: 'Revisa las calificaciones marcadas antes de guardar.',
      });
      return;
    }

    setSaving(true);
    setStatus({ type: 'info', message: 'Guardando resultados...' });

    try {
      const updated = await trainingReportApi.updateResults(
        idReporte,
        buildResultsPayload(rows),
        accessToken,
      );
      setReport(updated);
      const refreshedSummary = await trainingReportApi.summary(
        idReporte,
        accessToken,
      );
      setServerSummary(refreshedSummary);
      setFieldErrors({});
      setStatus({
        type: 'success',
        message: 'Resultados guardados correctamente.',
      });
    } catch (error) {
      console.error('No se pudieron guardar los resultados:', error);
      setStatus({
        type: 'error',
        message: error?.message || 'No fue posible guardar los resultados.',
      });
    } finally {
      setSaving(false);
    }
  };

  const statusClass =
    status?.type === 'success'
      ? 'admin-operation-status-success'
      : status?.type === 'error'
        ? 'admin-operation-status-error'
        : 'admin-operation-status-warning';

  return (
    <>
      <div className="admin-operation-panel-heading">
        <div>
          <p className="admin-operation-eyebrow">Paso 3 de 7</p>
          <h2>Resultados</h2>
          <p>
            Captura la calificación diagnóstica y final. Puedes usar valores de
            0 a 100, NP para “No presentó” o B para “Baja”.
          </p>
        </div>
        <span
          className={`training-report-status training-report-status-${report?.estado || 'borrador'}`}
        >
          {report?.estado === 'generado' ? 'Generado' : 'Borrador'}
        </span>
      </div>

      <div className="training-report-results-summary">
        <div>
          <span>Promedio grupal</span>
          <strong>
            {summary.promedio_grupal === null ||
            summary.promedio_grupal === undefined
              ? 'Pendiente'
              : summary.promedio_grupal}
          </strong>
        </div>
        <div>
          <span>Finales numéricos</span>
          <strong>{summary.resultados_finales_numericos ?? 0}</strong>
        </div>
        <div>
          <span>NP</span>
          <strong>{summary.resultados_finales_np ?? 0}</strong>
        </div>
        <div>
          <span>Bajas</span>
          <strong>{summary.resultados_finales_b ?? 0}</strong>
        </div>
        <div>
          <span>Pendientes</span>
          <strong>{summary.resultados_finales_sin_capturar ?? 0}</strong>
        </div>
      </div>

      {status && (
        <div className={`admin-operation-status ${statusClass}`} role="status">
          {status.message}
        </div>
      )}

      <div className="admin-operation-table-wrapper training-report-results-wrapper">
        <table className="admin-operation-table training-report-results-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Participante</th>
              <th>Calificación diagnóstica</th>
              <th>Calificación final</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const errors = fieldErrors[row.id_reporte_participante] || {};
              return (
                <tr key={row.id_reporte_participante}>
                  <td>{index + 1}</td>
                  <td>
                    <strong>{row.nombre_completo}</strong>
                    <small className="training-report-results-matricula">
                      {row.matricula_alumno}
                    </small>
                  </td>
                  <td>
                    <input
                      className={`training-report-result-input ${errors.calificacion_diagnostica ? 'training-report-result-input-error' : ''}`}
                      value={row.calificacion_diagnostica}
                      onChange={(event) =>
                        changeGrade(
                          row.id_reporte_participante,
                          'calificacion_diagnostica',
                          event.target.value,
                        )
                      }
                      onBlur={(event) =>
                        normalizeOnBlur(
                          row.id_reporte_participante,
                          'calificacion_diagnostica',
                          event.target.value,
                        )
                      }
                      placeholder="0–100, NP o B"
                      disabled={saving}
                      aria-label={`Calificación diagnóstica de ${row.nombre_completo}`}
                    />
                    {errors.calificacion_diagnostica && (
                      <small className="training-report-field-error">
                        {errors.calificacion_diagnostica}
                      </small>
                    )}
                  </td>
                  <td>
                    <input
                      className={`training-report-result-input ${errors.calificacion_final ? 'training-report-result-input-error' : ''}`}
                      value={row.calificacion_final}
                      onChange={(event) =>
                        changeGrade(
                          row.id_reporte_participante,
                          'calificacion_final',
                          event.target.value,
                        )
                      }
                      onBlur={(event) =>
                        normalizeOnBlur(
                          row.id_reporte_participante,
                          'calificacion_final',
                          event.target.value,
                        )
                      }
                      placeholder="0–100, NP o B"
                      disabled={saving}
                      aria-label={`Calificación final de ${row.nombre_completo}`}
                    />
                    {errors.calificacion_final && (
                      <small className="training-report-field-error">
                        {errors.calificacion_final}
                      </small>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="training-report-table-note">
        El promedio grupal considera únicamente calificaciones finales numéricas;
        NP, B y pendientes quedan fuera del promedio.
      </p>

      <div className="training-report-editor-actions">
        <button
          className="admin-operation-button admin-operation-button-secondary"
          type="button"
          onClick={onPrevious}
          disabled={saving}
        >
          Volver a participantes
        </button>
        <button
          className="admin-operation-button"
          type="button"
          onClick={saveResults}
          disabled={saving || rows.length === 0}
        >
          {saving ? 'Guardando...' : 'Guardar resultados'}
        </button>
      </div>
    </>
  );
};
